import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Modal, Skeleton } from '@mui/material';
import { enqueueSnackbar } from 'notistack';
import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined';
import DashboardOutlinedIcon from '@mui/icons-material/DashboardOutlined';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined';
import CasesAPI from 'api/CasesAPI';
import BeneficialOwnershipTicketResults from 'components/Monitor/ResultsTab/BeneficialOwnershipTicketResults';
import OwnershipTree from 'components/BeneficialOwnership/OwnershipTree';
import { beneficialOwnershipVisualPDF } from 'components/Common/BeneficialOwnershipVisualPdf';
import { GlobalContext } from 'providers/GlobalProvider';
import {
  buildOwnershipGraph,
  subjectConnectionCount,
} from 'utils/beneficialOwnershipGraph';
import {
  buildBeneficialOwnershipVisualData,
  hasNetworkToMap,
  NO_NETWORK_TO_MAP,
} from 'utils/beneficialOwnershipVisualData';
import { IconArrowRight } from 'pages/import/tools/ToolsIcons';
import 'pages/import/tools/importTools.css';
import { formatVerificationDate } from './verificationReportSummary';
import { OwnershipInvestigation } from './useOwnershipInvestigation';

interface EntityHubOwnershipReportProps {
  investigation: OwnershipInvestigation;
  /** The run-again control, gated on the team's trial the same way as the row. */
  runAgain: React.ReactNode;
  onBack: () => void;
}

/**
 * The Beneficial Ownership investigation's report, read from its ticket on the
 * Deep Dive itself (the ticket no longer lists in Saved Work): Saved Work's
 * own report sections. The
 * ownership tree opens in a window over the report, from the report bar or
 * from Ownership Found.
 */
const EntityHubOwnershipReport: React.FC<EntityHubOwnershipReportProps> = ({
  investigation,
  runAgain,
  onBack,
}) => {
  const { cur_user } = useContext(GlobalContext);
  const { ticketId, state, finishedAt, startedAt } = investigation;
  const [ticket, setTicket] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [treeOpen, setTreeOpen] = useState(false);
  const [downloadingVisual, setDownloadingVisual] = useState(false);

  const latestLoad = useRef(0);

  const load = useCallback(async () => {
    if (!ticketId) return;
    const thisLoad = latestLoad.current + 1;
    latestLoad.current = thisLoad;
    setLoading(true);
    setFailed(false);
    const response = await CasesAPI.getCase(ticketId);
    if (thisLoad !== latestLoad.current) return;
    if (response?.success && response.result?._id) {
      setTicket(response.result);
    } else {
      setFailed(true);
    }
    setLoading(false);
  }, [ticketId]);

  useEffect(() => {
    load();
  }, [load, finishedAt]);

  const reportOn =
    formatVerificationDate(finishedAt) || formatVerificationDate(startedAt);
  const review = state === 'needs-review' || state === 'interrupted';
  const graph = useMemo(
    () => (ticket ? buildOwnershipGraph(ticket) : null),
    [ticket],
  );
  const showTree = Boolean(graph && !graph.isEmpty);
  const treeCount = graph && showTree ? subjectConnectionCount(graph) : 0;
  const openTree = () => setTreeOpen(true);

  // The one-page visual summary, built from this ticket in the browser. It is
  // credited to the run's owner, like the filed report, or the person viewing.
  const preparedBy =
    [ticket?.owner?.firstName, ticket?.owner?.lastName]
      .filter(Boolean)
      .join(' ')
      .trim() ||
    [cur_user?.basics?.firstName, cur_user?.basics?.lastName]
      .filter(Boolean)
      .join(' ')
      .trim() ||
    cur_user?.basics?.email ||
    'BITE Beneficial Ownership';
  const visualData = useMemo(
    () =>
      ticket ? buildBeneficialOwnershipVisualData(ticket, preparedBy) : null,
    [ticket, preparedBy],
  );
  // Offered only when the report has a network to map.
  const visualUnavailable =
    Boolean(visualData) && !hasNetworkToMap(visualData!);

  const downloadVisual = async () => {
    if (!visualData || visualUnavailable || downloadingVisual) return;
    setDownloadingVisual(true);
    try {
      await beneficialOwnershipVisualPDF(
        visualData,
        `${ticket.ticketName || 'Beneficial Ownership'} - Visual Summary.pdf`,
      );
    } catch (error: any) {
      enqueueSnackbar(
        `Failed to generate visual summary PDF: ${
          error?.message || 'Unknown error'
        }`,
        { variant: 'error' },
      );
    } finally {
      setDownloadingVisual(false);
    }
  };

  return (
    <div className='iml-root iml-style-island iml-page is-ownership entity-hub-risk-results entity-hub-ownership-report'>
      <div className='iml-page-bar entity-hub-risk-head'>
        <button type='button' className='iml-back' onClick={onBack}>
          <IconArrowRight className='iml-back-icon' />
          Back to tools
        </button>
        <div className='entity-hub-risk-title'>
          <h2 className='iml-page-title'>
            <span className='iml-row-icon' aria-hidden='true'>
              <GroupsOutlinedIcon sx={{ fontSize: 18 }} />
            </span>
            Beneficial Ownership investigation
          </h2>
          <p className='tnum'>
            {`${
              reportOn ? `Report from ${reportOn}` : 'Latest report'
            } · saved to this record`}
          </p>
        </div>
        {showTree ? (
          <button
            type='button'
            className='entity-hub-btn entity-hub-tree-btn'
            onClick={openTree}
            aria-haspopup='dialog'
          >
            <AccountTreeOutlinedIcon />
            Ownership tree
            <span className='entity-hub-tree-count tnum'>{treeCount}</span>
          </button>
        ) : null}
        <button
          type='button'
          className='entity-hub-btn'
          onClick={downloadVisual}
          disabled={!visualData || visualUnavailable || downloadingVisual}
          title={visualUnavailable ? NO_NETWORK_TO_MAP : undefined}
        >
          <DashboardOutlinedIcon />
          {visualUnavailable
            ? 'No network to map'
            : downloadingVisual
            ? 'Preparing visual'
            : 'Download visual'}
        </button>
        {runAgain}
      </div>

      {review ? (
        <div className='entity-hub-ownership-banner' role='status'>
          <ReportProblemOutlinedIcon />
          <span>
            This run finished with problems. Some sections may be incomplete.
          </span>
        </div>
      ) : null}

      {loading && !ticket ? (
        <div className='entity-hub-ownership-skeleton' aria-busy='true'>
          <Skeleton variant='rounded' height={88} />
          <Skeleton variant='rounded' height={220} />
          <div className='entity-hub-ownership-skeleton-pair'>
            <Skeleton variant='rounded' height={180} />
            <Skeleton variant='rounded' height={180} />
          </div>
        </div>
      ) : failed && !ticket ? (
        <div className='entity-hub-ownership-error' role='alert'>
          <p>The report could not be loaded.</p>
          <button type='button' className='entity-hub-btn' onClick={load}>
            Try again
          </button>
        </div>
      ) : ticket ? (
        <>
          <BeneficialOwnershipTicketResults
            ticket={ticket}
            onViewTree={showTree ? openTree : undefined}
          />
          <Modal
            open={treeOpen && showTree}
            onClose={() => setTreeOpen(false)}
            aria-labelledby='entity-hub-ownership-tree-title'
            BackdropProps={{ className: 'bo-tree-backdrop' }}
          >
            <div
              className='bo-tree-dialog'
              role='dialog'
              aria-modal='true'
              aria-labelledby='entity-hub-ownership-tree-title'
            >
              <OwnershipTree
                ticket={ticket}
                dialog={{
                  titleId: 'entity-hub-ownership-tree-title',
                  meta: reportOn ? `Report from ${reportOn}` : undefined,
                  onClose: () => setTreeOpen(false),
                }}
              />
            </div>
          </Modal>
        </>
      ) : null}
    </div>
  );
};

export default EntityHubOwnershipReport;
